import { Table, Space, Button, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { ExportExcelButton, ColumnManagerDropdown } from '../../common';
import { useColumnManager } from '../../../hooks';
import * as XLSX from 'xlsx';

const PaginatedTableStock = ({ csvName, getTableData, getNroCliente }: any) => {

    // Fetch Data
    const [fullTableData, setFullTableData] = useState<any[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<any[]>(fullTableData)
    const [loading, setLoading] = useState(false)

    const {user, getAccessTokenSilently} = useAuth0();

    const id_empresa = user?.id_empresa
    const id_cliente = user?.id_cliente
    const tipo_usuario = user?.tipo_usuario 
  
    const variables_api = `${tipo_usuario}/${id_empresa}/${id_cliente}`

    // Para filtrar
    const desired_output = (data: any, column: any) => {
        const unique_values = data
            .map((item: any) => item[column])
            .filter(
                (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
            );
        return unique_values;
    };

    // Para cuando se filtre la tabla desde la tabla.
    // En lugar de modificar la tabla completa, usamos otra variable.
    const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
        setFilteredTableData(extra.currentDataSource);
        getNroCliente(filters.nro_cliente)
      };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/stock/${variables_api}`;
          const token = await getAccessTokenSilently();
          const response = await fetch(apiEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const data = await response.json();
          // Compartimos y guardamos info.
          setFullTableData(Array.isArray(data) ? data : []);
          setFilteredTableData(Array.isArray(data) ? data : []);
          getTableData(Array.isArray(data) ? data : []);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      };
      if (variables_api) {
        fetchData();
      }
    }, [variables_api, getAccessTokenSilently]);

    // Clientes únicos para el filtro.
    const clientesUnicos = desired_output(fullTableData, 'denominacion_cliente')
                                .map((denominacion_cliente: any) => ({text: denominacion_cliente, value: denominacion_cliente}));
    const ciclosUnicos = desired_output(fullTableData, 'ciclo')
                                .map((ciclo: any) => ({text: ciclo, value: ciclo}));

    // Table
    type DataType = {
        denominacion_cliente : string
        ciclo : string
        fardos_producidos : number
        fardos_despachados : number
        fardos_stock : number
        kg_producidos : number
        kg_despachados : number
        kg_stock : number
      }
      
    const allColumns: TableColumnsType<DataType> = [
      {
        title: 'Cliente',
        dataIndex: 'denominacion_cliente',
        key: 'denominacion_cliente',
        width: 150,
        ellipsis: true,
        render: (text) => <i>{text}</i>,
  
        filters: clientesUnicos,
        onFilter: (value, record) => record.denominacion_cliente.includes(value as string),
      },
      {
        title: 'Ciclo',
        dataIndex: 'ciclo',
        key: 'ciclo',
        width: 100,
        ellipsis: true,
        filters: ciclosUnicos,
        onFilter: (value, record) => record.ciclo.includes(value as string),
  
        defaultSortOrder: 'descend',
      },
      {
        title: 'Fard. Produc.',
        dataIndex: 'fardos_producidos',
        key: 'fardos_producidos',
        width: 120,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.fardos_producidos - b.fardos_producidos,
      },
      {
        title: 'Fard. Despach.',  
        dataIndex: 'fardos_despachados',
        key: 'fardos_despachados',
        width: 120,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.fardos_despachados - b.fardos_despachados,
      },
      {
        title: 'Fard. Stock',
        dataIndex: 'fardos_stock',
        key: 'fardos_stock',
        width: 100,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.fardos_stock - b.fardos_stock,
      },
      {
        title: 'Kg Produc.',
        dataIndex: 'kg_producidos',
        key: 'kg_producidos',
        width: 110,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.kg_producidos - b.kg_producidos,
      },
      {
        title: 'Kg Despach.',
        dataIndex: 'kg_despachados',
        key: 'kg_despachados',
        width: 110,
        render: (text) => new Intl.NumberFormat('es-AR').format(text),
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.kg_despachados - b.kg_despachados,
      },
      {
        title: 'Kg Stock',
        dataIndex: 'kg_stock',
        key: 'kg_stock',
        width: 110,
  
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.kg_stock - b.kg_stock,
      },
    ]

    // Lista de todas las columnas disponibles (keys)
    const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

    // Nombres amigables para las columnas
    const columnNames: Record<string, string> = {
        'denominacion_cliente': 'Cliente',
        'ciclo': 'Ciclo',
        'fardos_producidos': 'Fard. Produc.',
        'fardos_despachados': 'Fard. Despach.',
        'fardos_stock': 'Fard. Stock',
        'kg_producidos': 'Kg Produc.',
        'kg_despachados': 'Kg Despach.',
        'kg_stock': 'Kg Stock'
    };

    // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
    const columnManager = useColumnManager({
        storageKey: 'stock',
        allKeys: allColumnKeys,
    });

    // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
    const filteredColumns = useMemo(() => {
        const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
        return columnManager.orderedVisibleKeys
            .map(key => columnsByKey.get(key))
            .filter((col): col is (typeof allColumns)[number] => Boolean(col));
    }, [columnManager.orderedVisibleKeys, allColumns]);

    const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any) => {
        return {
          denominacion_cliente : fila.denominacion_cliente,
          ciclo : fila.ciclo,
          fardos_producidos : fila.fardos_producidos,
          fardos_despachados : fila.fardos_despachados,
          fardos_stock : fila.fardos_stock,
          kg_producidos : fila.kg_producidos,
          kg_despachados : fila.kg_despachados,
          kg_stock : fila.kg_stock,
        }
    });

    // Función para exportar a Excel
    const handleExportExcel = () => {
        try {
            const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
            const exportData = sourceData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                columnManager.orderedVisibleKeys.forEach(key => {
                    exportRow[columnNames[key] || key] = row[key];
                });
                return exportRow;
            });
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock');
            XLSX.writeFile(workbook, csvName || `stock_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
        }
    };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            bordered
            size="middle"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
            >
        </Table>
      </div>
    );
  };
  
export default withAuthenticationRequired(PaginatedTableStock);
